import Dropzone from "components/dropzone";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import Textarea from "components/textarea";
import { TextEditor } from "components/textEditor";
import { IAttractionAddtionalSetupForm, ISimpleEventDetail, QRCodeType, UserType } from "interfaces";
import { get } from "lodash";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { LANGUAGES, QR_CODE_TYPES } from "utills/constants";
import { canAccessByType, sanitizeHtml } from "utills/helpers";

export const BasicInformation = ({ isEdit, eventDetailData }: { isEdit: boolean, eventDetailData: ISimpleEventDetail }) => {
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IAttractionAddtionalSetupForm>(); // retrieve all hook methods
  const [activeTab, setActiveTab] = useState("edit");

  return isEdit ? (
    <div className="box-container p-4 sm:p-6">
      <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
        <div className="sm:col-span-2 w-fit">
          <Controller
            control={control}
            name="basic_information.is_ignore_participant_step"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton 
                label="Hide Input Participant Info"
                hint="If you are not using the participant information step on ActiUp web, please turn on the switch button. <br />If you are using the participant information step, please turn off the switch button."
                checked={value}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div className="sm:col-span-2 w-fit">
          <Controller
            control={control}
            name="basic_information.is_hide_vat"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton 
                label="Hide Tax Information Input"
                hint="If the event does not issue VAT invoices, please turn on the switch button. <br/>If the event issues VAT invoices, please turn off the switch button."               
                checked={value}
                onChange={onChange}
              />
            )}
          />
        </div>
        {canAccessByType(UserType.ADMIN) && (
          <div className="sm:col-span-2 w-fit">
            <Controller
              control={control}
              name="basic_information.use_authentication"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label="Use Authentication"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
        )}
        {canAccessByType(UserType.ADMIN) && (
          <div className="sm:col-span-2 w-fit">
            <Controller
              control={control}
              name="basic_information.use_pass_tool"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label="Use Pass tool"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
        )}
        <div className="sm:col-span-4 w-fit flex flex-row items-center gap-x-4">
          <Controller
            control={control}
            name="basic_information.is_send_qr_code"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton 
                label="Send QR code"
                hint="If the event uses online QR e-tickets, please turn on the switch button. <br />If the event does not use online QR e-tickets, please turn off the switch button."
                checked={value}
                onChange={(isChecked) => {
                  onChange(isChecked);
                  if (!isChecked) {
                    setValue("basic_information.qr_code_type", null);
                  } else {
                    setValue("basic_information.qr_code_type", QRCodeType.GENERATE);
                  }
                }}
              />
            )}
          />
          {watch("basic_information.is_send_qr_code") ? (
            <Controller
              control={control}
              name={`basic_information.qr_code_type`}
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <Select
                    key={`basic_information.qr_code_type`}
                    inputRef={ref}
                    options={QR_CODE_TYPES}
                    id={`basic_information.qr_code_type`}
                    placeholder="Select QR Code Type"
                    defaultValue={
                      QR_CODE_TYPES.find(
                        (selected: { label: string; value: any }) =>
                          selected.value === value
                      ) ?? ""
                    }
                    value={QR_CODE_TYPES.find(
                      (selected: { label: string; value: any }) =>
                        selected.value === value
                    )}
                    onChange={(selected: { label: string; value: any }) => {
                      onChange(selected?.value);
                    }}
                    error={get(errors, `basic_information.qr_code_type`, "")}
                  />
                );
              }}
            />
          ) : <></>}

        </div>

        {canAccessByType(UserType.ADMIN) && (
          <>
            <div className="col-span-full w-fit">
              <Controller
                control={control}
                name="basic_information.is_static_html"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    label="Is Static HTML"
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
              {get(errors, "basic_information.use_pass_tool", "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `basic_information.use_pass_tool.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            {watch("basic_information.is_static_html") && (
              <div className="col-span-full">
                <Controller
                  control={control}
                  name="basic_information.static_html_content"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <div className="flex flex-col gap-0">
                        {/* Tab Header */}
                        <div className="flex border border-b-0 border-gray-200 rounded-t-md overflow-hidden w-fit">
                          <button
                            type="button"
                            onClick={() => setActiveTab("edit")}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                              activeTab === "edit"
                                ? "bg-white text-gray-900 border-b-2 border-b-blue-500"
                                : "bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveTab("preview")}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                              activeTab === "preview"
                                ? "bg-white text-gray-900 border-b-2 border-b-blue-500"
                                : "bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            Preview
                          </button>
                        </div>

                        {/* Tab Content */}
                        <div className="border border-gray-200 rounded-b-md rounded-tr-md">
                          {activeTab === "edit" ? (
                            <Textarea
                              id="basic_information.static_html_content"
                              placeholder="Enter static HTML content"
                              error={get(errors, `basic_information.static_html_content`, "")}
                              onChange={(e: { target: { value: string; }; }) => onChange(sanitizeHtml(e.target.value))}
                              value={value}
                              className="rounded-none rounded-b-md border-0 min-h-[320px]"
                            />
                          ) : (
                            <iframe
                              srcDoc={value || "<p style='color:#9ca3af;font-size:14px;padding:12px'>Nothing to preview</p>"}
                              className="w-full min-h-[400px] border-0 rounded-b-md rounded-tr-md"
                              title="HTML Preview"
                            />
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                {errors["basic_information"]?.static_html_content && (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `basic_information.static_html_content.message`, "")}
                    </span>
                  </p>
                )}
              </div>
            )}
          </>
        )}

        <div className="col-span-full">
          <Label
            id="basic_information.event_term_url"
            name="Event T&C link"
            hint="Input the terms and conditions link for the event. This will be used in the Payment Method selection step in the ticket purchase flow on the ActiUp website."
            required
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div
                  className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                >
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="w-4 shrink-0"
                  />
                  <p>
                    {
                      LANGUAGES.find(
                        (language) =>
                          language.value === lang
                      )?.label
                    }
                  </p>
                </div>
                <Controller
                  name={`basic_information.event_term_url.${lang}`}
                  control={control}
                  render={({ field: { onChange, value = "" } }) => (
                    <TextEditor
                      key={`basic_information.event_term_url.${lang}`}
                      placeholder={`Event T&C link (${lang})`}
                      value={value ?? ""}
                      onChange={onChange}
                      maxHeight={360}
                    />
                  )}
                />
                {get(
                  errors,
                  `basic_information.event_term_url.${lang}`,
                  ""
                ) ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `basic_information.event_term_url.${lang}.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.hold_ticket_time"
            name="Hold Ticket time"
            hint="The ticket hold time in steps 2 and 3 when the user makes a ticket purchase on the ActiUp website. The time will be counted in minutes."
            required
          />
          <Input
            id={`basic_information.hold_ticket_time`}
            type="number"
            error={get(errors, `basic_information.hold_ticket_time`, "")}
            placeholder="Hold Ticket Time"
            {...register("basic_information.hold_ticket_time", {
              valueAsNumber: true
            })}
            min={5}
          />
          {get(errors, `basic_information.hold_ticket_time`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `basic_information.hold_ticket_time.message`, "")}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.max_tickets_to_buy"
            name="Max tickets quantity per account"
            hint="Set the maximum number of tickets allowed to be purchased per ActiUp account. If there is no limit, please leave it blank."
          />
          <Input
            id={`basic_information.max_tickets_to_buy`}
            type="number"
            disabled={!watch("basic_information.use_authentication")}
            error={get(errors, `basic_information.max_tickets_to_buy`, "")}
            placeholder="Max tickets quantity per account"
            {...register("basic_information.max_tickets_to_buy", {
              valueAsNumber: true
            })}
            min={0}
          />
          {get(errors, `basic_information.max_tickets_to_buy`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `basic_information.max_tickets_to_buy.message`, "")}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.order_min_qty_ticket"
            name="Min tickets quantity per order"
            required
          />
          <Input
            id={`basic_information.order_min_qty_ticket`}
            type="number"
            error={get(errors, `basic_information.order_min_qty_ticket`, "")}
            placeholder="Min tickets quantity per order"
            min={1}
            {...register("basic_information.order_min_qty_ticket", {
              valueAsNumber: true
            })}
          />
          {get(errors, `basic_information.order_min_qty_ticket`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `basic_information.order_min_qty_ticket.message`, "")}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.order_max_qty_ticket"
            name="Max tickets quantity per order"
            required
          />
          <Input
            id={`basic_information.order_max_qty_ticket`}
            type="number"
            error={get(errors, `basic_information.order_max_qty_ticket`, "")}
            placeholder="Max tickets quantity per order"
            min={1}
            {...register("basic_information.order_max_qty_ticket", {
              valueAsNumber: true
            })}
          />
          {get(errors, `basic_information.order_max_qty_ticket`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `basic_information.order_max_qty_ticket.message`, "")}
              </span>
            </p>
          ) : null}
        </div>

        <div className="col-span-full">
          <Label
            id="basic_information.note_individual_ticket_purchase_flow"
            name="Note in the ticket purchase flow"
            hint="Used to set notes in the ticket purchase flow on the ActiUp website."
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div
                  className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                >
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="w-4 shrink-0"
                  />
                  <p>
                    {
                      LANGUAGES.find(
                        (language) =>
                          language.value === lang
                      )?.label
                    }
                  </p>
                </div>
                <Controller
                  control={control}
                  name={`basic_information.note_individual_ticket_purchase_flow.${lang}`}
                  render={({
                    field: { onChange, value = "" },
                  }) => (
                    <TextEditor
                      key={`basic_information.note_individual_ticket_purchase_flow.${lang}`}
                      value={value ?? ""}
                      onChange={onChange}
                    />
                  )}
                />

                {get(
                  errors,
                  `basic_information.note_individual_ticket_purchase_flow.${lang}`,
                  ""
                ) ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `basic_information.note_individual_ticket_purchase_flow.${lang}.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-full">
          <div>
            <Label id="basic_information.seat_map_url" name="Seat map image" />
            <Controller
              control={control}
              name={`basic_information.seat_map_url`}
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <Dropzone
                    id={`basic_information.seat_map_url`}
                    onChange={(imageUrl: string) => onChange(imageUrl)}
                    value={value}
                  />
                );
              }}
            />
            <p className="mt-1 text-sm text-[#868E95]">
              Maximum file size: 2MB <span className="mx-2">•</span> Supported image files: PNG, JPEG, JPG
            </p>
          </div>
          {get(errors, `basic_information.seat_map_url`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `basic_information.seat_map_url.message`, "")}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full">
          <Label
            id="basic_information.special_template"
            name="Special template key"
            hint="Applies to events using a different ticket email template than the system. Enter the template key to use."
          />
          <Input
            id={`basic_information.special_template`}
            error={get(errors, `basic_information.special_template`, "")}
            placeholder="Special template key"
            {...register("basic_information.special_template")}
          />
          {get(errors, `basic_information.special_template`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `basic_information.special_template.message`, "")}
              </span>
            </p>
          ) : null}
        </div>

      </div>
    </div>
  ) : <></>;
};
